// features/diagnosis/addUpdateDiagnosis.modal.tsx
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useMutation, useQueryClient } from 'react-query'

import Button from 'components/form/button'
import Input from 'components/form/input'
import Modal from 'components/modal'
import { privateRequest } from 'config/axios.config'
import { errorHandler } from 'utils/errorHandler'

// Local types
export type Diagnosis = {
    _id: string
    name: string
    type?: string
    company_name?: string
    createdAt: Date
    updatedAt: Date
}

interface Payload {
    name: string
}

interface UpdatePayload extends Payload {
    id: string
}

export default NiceModal.create(
    ({ diagnosis, title }: { diagnosis?: Diagnosis; title: string }) => {
        const modal = useModal()
        const queryClient = useQueryClient()

        const [form, setForm] = useState({ name: '' })
        const [errors, setErrors] = useState<{ name?: string }>({})

        useEffect(() => {
            if (!diagnosis) return
            setForm({ name: diagnosis.name ?? '' })
        }, [diagnosis])

        const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
            setForm({ ...form, [e.target.name]: e.target.value })
            setErrors({})
        }

        const addMutation = useMutation<{ message: string }, Error, Payload>(
            async (payload) => {
                try {
                    const res = await privateRequest.post('admin/diagnosis', payload)
                    return res.data
                } catch (error) {
                    errorHandler(error)
                    throw error
                }
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('diagnosis')
                    modal.remove()
                },
            }
        )

        const updateMutation = useMutation<{ message: string }, Error, UpdatePayload>(
            async (payload) => {
                try {
                    const res = await privateRequest.patch('admin/diagnosis', payload)
                    return res.data
                } catch (error) {
                    errorHandler(error)
                    throw error
                }
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('diagnosis')
                    modal.remove()
                },
            }
        )

        const submitHandler = (e: React.FormEvent) => {
            e.preventDefault()
            if (!form.name) {
                setErrors({ name: 'Name is required' })
                return
            }

            const payload = { name: form.name }

            if (diagnosis?._id) {
                toast.promise(updateMutation.mutateAsync({ ...payload, id: diagnosis._id }), {
                    loading: 'Updating...',
                    success: (res) => res.message ?? 'Updated successfully',
                    error: (err: any) => err.message ?? 'Something went wrong',
                })
            } else {
                toast.promise(addMutation.mutateAsync(payload), {
                    loading: 'Adding...',
                    success: (res) => res.message ?? 'Added successfully',
                    error: (err: any) => err.message ?? 'Something went wrong',
                })
            }
        }

        return (
            <Modal title={title} visible={modal.visible} onCancel={() => modal.remove()} className="max-w-2xl">
                <form onSubmit={submitHandler} className="flex flex-col gap-6">
                    <Input
                        name="name"
                        label="Name"
                        placeholder="Enter Diagnosis Name"
                        value={form.name}
                        onChange={changeHandler}
                        error={!!errors.name}
                        helpText={errors.name}
                    />
                    <Button fullWidth disabled={addMutation.isLoading || updateMutation.isLoading}>
                        Submit
                    </Button>
                </form>
            </Modal>
        )
    }
)
